/**
 * useGhcpOAuthConnection Hook
 *
 * Thin wrapper around useRepoOAuthConnection for creating GitHub Copilot
 * agent connections. Uses the same ottogithub connector and OAuth consent
 * infrastructure as repo-clone connections, but creates a separate
 * connection resource scoped for LLM use.
 *
 * The user sees "GitHub Copilot" as the provider label — "ottogithub" is
 * an implementation detail never surfaced in UI.
 */

import { useCallback } from 'react';
import { useRepoOAuthConnection } from '@/hooks/useRepoOAuthConnection';
import type { RepoOAuthResult, RepoOAuthFlowState } from '@/hooks/useRepoOAuthConnection';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppStore } from '@/stores/appStore';

// =============================================================================
// TYPES
// =============================================================================

export interface GhcpOAuthParams {
  /** Display name for the connection (user-chosen) */
  connectionName: string;
}

export interface UseGhcpOAuthConnectionReturn {
  /** Current OAuth flow state */
  state: RepoOAuthFlowState;
  /** Error message if flow failed */
  error: string | null;
  /** Result on success */
  result: RepoOAuthResult | null;
  /** Start the GitHub OAuth consent flow */
  connect: (params: GhcpOAuthParams) => Promise<RepoOAuthResult | null>;
  /** Cancel in-progress flow */
  cancel: () => void;
  /** Reset to idle */
  reset: () => void;
  /** Whether a flow is in progress */
  isInProgress: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for creating GitHub Copilot agent connections via OAuth.
 *
 * Uses the same ottogithub connector as repo-clone, but creates a separate
 * connection resource. The connection name is user-provided (same UX as
 * naming an OpenAI connection).
 */
export function useGhcpOAuthConnection(): UseGhcpOAuthConnectionReturn {
  const repoOAuth = useRepoOAuthConnection();
  // Prefer route-derived project (consistent with useRepoOAuthConnection internals)
  // over the store value, which may be bridged asynchronously.
  const { projectName: routeProjectName } = useCurrentProject();
  const storeProjectName = useAppStore((s) => s.selectedProject ?? '');
  const projectName = routeProjectName || storeProjectName;

  const connect = useCallback(
    async (params: GhcpOAuthParams): Promise<RepoOAuthResult | null> => {
      // Let useRepoOAuthConnection surface a descriptive error if project scope is missing.
      return repoOAuth.connect({
        connectorType: 'github',
        automationProjectId: projectName,
        connectionName: params.connectionName,
      });
    },
    [repoOAuth, projectName],
  );

  return {
    state: repoOAuth.state,
    error: repoOAuth.error,
    result: repoOAuth.result,
    connect,
    cancel: repoOAuth.cancel,
    reset: repoOAuth.reset,
    isInProgress: repoOAuth.isInProgress,
  };
}
